import { useState, useEffect } from 'react'
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth'
import { doc, setDoc, getDoc, collection, getDocs, onSnapshot } from 'firebase/firestore'
import { useDispatch, useSelector } from 'react-redux'
import { login, logout, setUserData, setLibraryItems } from '../store/authSlice'
import { auth, db } from '../firebase/config'

// Set to true for testing purposes only, set to false in production
const TEST_MODE = false;

export function useAuth() {
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()

  // Function to fetch library items
  const fetchLibraryItems = async (userId) => {
    try {
      const libraryRef = collection(db, 'users', userId, 'library')
      const librarySnapshot = await getDocs(libraryRef)
      const libraryItems = librarySnapshot.docs.map(doc => doc.id)
      dispatch(setLibraryItems(libraryItems))
    } catch (err) {
      console.error('Error fetching library:', err)
    }
  }

  useEffect(() => {
    let unsubscribeLibrary = null

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Store userId in localStorage for Stripe
          localStorage.setItem('userId', user.uid)

          const userDoc = await getDoc(doc(db, 'users', user.uid))
          let userData = userDoc.data()

          // If user document doesn't exist, create it with default values
          if (!userData) {
            userData = {
              email: user.email,
              role: 'researcher',
              createdAt: new Date().toISOString()
            }
            await setDoc(doc(db, 'users', user.uid), userData)
          }

          // Dispatch login with persisted auth state
          dispatch(login({
            uid: user.uid, // Keep uid consistent with Firebase
            id: user.uid,  // Keep id for backward compatibility
            email: user.email,
            role: userData?.role || 'researcher',
            username: userData?.username || user.displayName,
            displayName: userData?.displayName || user.displayName,
            createdAt: userData?.createdAt || new Date().toISOString()
          }))

          // Set user data with persisted values
          dispatch(setUserData({
            role: userData?.role || 'researcher',
            username: userData?.username || user.displayName,
            displayName: userData?.displayName || user.displayName
          }))

          // Subscribe to real-time user document updates
          const userRef = doc(db, 'users', user.uid);
          const unsubscribeDoc = onSnapshot(userRef, (snapshot) => {
            const updatedData = snapshot.data();
            if (updatedData) {
              dispatch(setUserData({
                role: updatedData.role || 'researcher',
                username: updatedData.username || user.displayName,
                displayName: updatedData.displayName || user.displayName
              }));
            }
          });

          return () => {
            if (unsubscribeDoc) {
              unsubscribeDoc();
            }
          };
        } catch (err) {
          console.error('Error fetching user data:', err)
        }
      } else {
        // Clear userId from localStorage on logout
        localStorage.removeItem('userId')
        dispatch(logout())
        
        // Clean up library listener
        if (unsubscribeLibrary) {
          unsubscribeLibrary()
          unsubscribeLibrary = null
        }
      }
    })

    return () => {
      unsubscribeAuth()
      if (unsubscribeLibrary) {
        unsubscribeLibrary()
      }
    }
  }, [dispatch])

  const signUp = async (email, password, username) => {
    setLoading(true)
    setError(null)
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password)
      
      // Update user profile with display name
      await updateProfile(user, {
        displayName: username
      });
      
      const userDoc = {
        email,
        username,
        role: 'researcher',
        displayName: username,
        createdAt: new Date().toISOString()
      }
      
      // Create user document
      await setDoc(doc(db, 'users', user.uid), userDoc)

      // Store userId in localStorage
      localStorage.setItem('userId', user.uid)
      
      // Dispatch login action
      dispatch(login({
        uid: user.uid, // Keep uid consistent with Firebase
        id: user.uid,  // Keep id for backward compatibility
        email: user.email,
        role: userDoc.role,
        createdAt: userDoc.createdAt
      }))

      // Set user data
      dispatch(setUserData({
        role: userDoc.role,
        username: userDoc.username,
        displayName: userDoc.displayName
      }))
      
      return { success: true, user }
    } catch (err) {
      console.error('Sign up error:', err)
      const errorMessage = getErrorMessage(err.code)
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    setLoading(true)
    setError(null)
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password)
      
      // Get user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      const userData = userDoc.data()

      // Store userId in localStorage
      localStorage.setItem('userId', user.uid)
      
      // Dispatch login action
      dispatch(login({
        uid: user.uid, // Keep uid consistent with Firebase
        id: user.uid,  // Keep id for backward compatibility
        email: user.email,
        role: userData?.role || 'researcher',
        username: userData?.username || user.displayName,
        displayName: userData?.displayName || user.displayName,
        createdAt: userData?.createdAt
      }))

      // Set user data
      dispatch(setUserData({
        role: userData?.role || 'researcher',
        username: userData?.username || user.displayName,
        displayName: userData?.displayName || user.displayName
      }))

      return user
    } catch (err) {
      console.error('Sign in error:', err)
      setError(getErrorMessage(err.code))
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signOutUser = async () => {
    setError(null)
    try {
      await signOut(auth)
      // Clear userId from localStorage
      localStorage.removeItem('userId')
      dispatch(logout())
    } catch (err) {
      console.error('Sign out error:', err)
      setError(getErrorMessage(err.code))
      throw err
    }
  }

  const user = useSelector(state => state.auth.user)
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated)

  return {
    user,
    isAuthenticated,
    isAdmin: user?.role === 'admin',
    signUp,
    signIn,
    signOutUser,
    error,
    loading
  }
}

function getErrorMessage(errorCode) {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please login or use a different email.'
    case 'auth/invalid-email':
      return 'Invalid email address.'
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled. Please contact support.'
    case 'auth/weak-password':
      return 'Password must be at least 6 characters long.'
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.'
    case 'auth/user-not-found':
      return 'No account found with this email.'
    case 'auth/wrong-password':
      return 'Incorrect password.'
    default:
      return 'An error occurred. Please try again.'
  }
}
